import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function TermsOfUsePage() {
  const { language } = useLanguage();
  const isLV = language === 'lv';

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="terms-of-use-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {isLV ? 'Atpakaļ uz sākumu' : 'Back to home'}
        </Link>

        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#D97736] flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
                {isLV ? 'Lietošanas noteikumi' : 'Terms of Use'}
              </h1>
              <p className="text-sm text-[#5C5C5C]">
                {isLV ? 'Spēkā no 2026. gada' : 'Effective 2026'}
              </p>
            </div>
          </div>

          <article className="prose prose-sm max-w-none text-[#2B2B2B] leading-relaxed space-y-5">
            {isLV ? (
              <>
                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">1. Par platformu</h2>
                  <p>
                    KoLV (<strong>kolv.lv</strong>) ir Latvijas tiešsaistes tirgusvieta, kas savieno
                    pircējus un pārdevējus (fiziskas personas, IK, SIA). Platformas uzturētājs:
                    <strong> SIA “Gatis”</strong>, reģ.nr. 50203491851, juridiskā adrese
                    Toma iela 7 - 44, Liepāja, LV-3401. Reģistrējoties vai izmantojot platformu,
                    Tu piekriti šiem noteikumiem.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">2. Konts un reģistrācija</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Konts ir personisks, to nedrīkst nodot citai personai.</li>
                    <li>E-pasts jāapstiprina pirms kontu var lietot.</li>
                    <li>Minimālais vecums — 18 gadi (vai 16+ ar vecāku atļauju).</li>
                    <li>Tu esi atbildīgs par savas paroles drošību.</li>
                    <li>Mēs varam bloķēt kontu, ja tiek pārkāpti noteikumi vai likums.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">3. Pārdevēja pienākumi</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Sniegt patiesu informāciju par precēm/pakalpojumiem.</li>
                    <li>Norādīt savu juridisko formu (Fiziska persona / IK / SIA).</li>
                    <li>SIA un IK — norādīt reģ.nr. un (ja attiecas) PVN numuru.</li>
                    <li>Izpildīt pasūtījumus norunātajā termiņā.</li>
                    <li>Ievērot patērētāju tiesības, tajā skaitā 14 dienu atteikuma tiesības (attālinātā tirdzniecībā).</li>
                    <li>Nepārdot aizliegtas preces (narkotikas, ieroči bez licences, viltojumi, cilvēku orgāni u.c.).</li>
                    <li>Uzglabāt izdotos rēķinus un izpildīt VID prasības.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">4. Pircēja pienākumi</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Maksāt par pasūtītajām precēm laikā.</li>
                    <li>Sniegt pareizu piegādes informāciju.</li>
                    <li>Apieties cieņpilni saziņā ar pārdevēju.</li>
                    <li>Iesniegt pamatotas atsauksmes — ne nepatiesas, ne aizskarošas.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">5. Komisija un maksājumi</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>10% platformas komisija</strong> no katras veiksmīgas pārdošanas — automātiski atskaitīta pārdevēja maciņā.</li>
                    <li><strong>€0.40 servisa maksa</strong> tiek pievienota pircēja gala summai, kad maksā ar karti caur Stripe (nosedz Stripe apstrādes maksas un platformas uzturēšanu). <em>Iekšējiem darījumiem no maciņa uz maciņu šī maksa netiek piemērota.</em></li>
                    <li><strong>Izmaksas uz banku caur Stripe</strong>: pirmā izmaksa kalendārajā mēnesī ir bez maksas. Katra nākamā tajā pašā mēnesī — <strong>€0.50 apkalpošanas maksa</strong>, kas tiek atskaitīta no maciņa pirms naudas pārskaitījuma. Minimālā izmaksas summa — <strong>€5.00</strong>.</li>
                    <li><strong>Privātpersonu mēneša izmaksu limits: €250</strong> (DAC7 un AML noteikumi). Lai izmaksātu vairāk, jāreģistrē saimnieciskās darbības forma (pašnodarbinātais vai SIA).</li>
                    <li>Premium plāni (Basic, Pro) — maksa norādīta cenrādī.</li>
                    <li>Sezonālo katalogu iekļaušana — atsevišķa maksa pēc cenrāža.</li>
                    <li>Maksājumus apstrādā <strong>Stripe</strong>. Maksājumu kartes dati netiek glabāti mūsu serveros.</li>
                    <li>Detalizēta komisiju struktūra ar piemēriem: <Link to="/maksas-un-komisijas" className="text-[#D97736] underline">/maksas-un-komisijas</Link></li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">6. Atteikuma tiesības un strīdi</h2>
                  <p>
                    Pircējam — patērētājam — ir <strong>14 dienu atteikuma tiesības</strong> atbilstoši
                    LR Patērētāju tiesību aizsardzības likumam un ES Direktīvai 2011/83/ES,
                    izņemot gadījumus, kad prece ir pēc pasūtījuma izgatavota, ātri bojājas vai izpakota (higiēnas preces).
                  </p>
                  <p>
                    Strīdus pirmām kārtām risina starp pircēju un pārdevēju. Ja neizdodas, pircējs
                    var vērsties{' '}
                    <a href="https://www.ptac.gov.lv" className="text-[#D97736] underline">Patērētāju tiesību aizsardzības centrā</a>{' '}
                    vai ES strīdu risināšanas platformā{' '}
                    <a href="https://ec.europa.eu/consumers/odr/" className="text-[#D97736] underline">ec.europa.eu/consumers/odr</a>.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">7. Aizliegtās darbības</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Viltotu vai ar autortiesībām aizsargātu preču tirgošana bez tiesībām.</li>
                    <li>Spams, skaidras naudas mazgāšana, krāpšana.</li>
                    <li>Mēģinājumi apiet komisiju (piem., aicinājumi pircēju maksāt ārpus platformas).</li>
                    <li>Uzbrukumi sistēmai (DDoS, skrāpšana, kodu injekcija).</li>
                    <li>Aizskarošs, diskriminējošs vai naida saturs.</li>
                  </ul>
                  <p>Par pārkāpumiem mēs bloķējam kontu bez brīdinājuma.</p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">8. Atbildības ierobežojumi</h2>
                  <p>
                    KoLV darbojas kā tirgusvieta — tehniski savedam pircēju un pārdevēju. Mēs neesam
                    preces ražotājs/tirgotājs un neesam līgumslēdzēja puse darījumā starp pircēju un
                    pārdevēju. KoLV nevarēs atbildēt par preces kvalitāti, piegādi vai garantijām —
                    tas ir pārdevēja pienākums saskaņā ar likumu.
                  </p>
                  <p>
                    Mēs centīsimies nodrošināt platformas pieejamību 99%+ laika, bet garantēt
                    to nevaram (profilaktika, DDoS u.c.).
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">9. Intelektuālais īpašums</h2>
                  <p>
                    Platformas dizains, kods, logotips — pieder KoLV. Sludinājumu saturs (attēli,
                    apraksti) pieder pārdevējiem, kas mums piešķir neekskluzīvu licenci to rādīt
                    platformā un mārketinga materiālos.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">10. Noteikumu maiņa</h2>
                  <p>
                    Par būtiskām izmaiņām paziņosim pa e-pastu vismaz 30 dienas iepriekš.
                    Turpinot lietot platformu, Tu piekriti jaunajiem noteikumiem.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">11. Piemērojamie tiesību akti</h2>
                  <p>
                    Šie noteikumi pakļauti Latvijas Republikas likumdošanai. Strīdi risināmi Latvijas
                    tiesās, ja vien patērētāja tiesības neparedz citādi.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">12. Kontakti</h2>
                  <p>
                    SIA “Gatis” (reģ.nr. 50203491851)<br />
                    Toma iela 7 - 44, Liepāja, LV-3401<br />
                    E-pasts: <strong>kolatvietim@gmail.com</strong><br />
                    Vietne: <a href="https://kolv.lv" className="text-[#D97736] underline">kolv.lv</a>
                  </p>
                </section>
              </>
            ) : (
              <p className="text-[#5C5C5C]">
                English version available on request. Please see the Latvian version above for full details.
              </p>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}
